import type { ReactElement } from "react";
import { useState } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { FormSelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/FormSelectCombobox";
import { FormFieldLabel } from "app/pages/sales-intelligence/common-components/form-fields";
import type { IFilterViewBaseModelProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import getUpdateDateOptionLabel from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/UpdateDateFilter/utils/getUpdateDateOptionLabel";
import type { IAppsUpdateDateDescriptionDto } from "app/pages/sales-intelligence/modules/find-apps-filters/types/description.dto";
import type { IAppsUpdateDateFilterDto } from "app/pages/sales-intelligence/store/find-apps/types";
import type { RelativeRange, RelativeRangeType } from "app/pages/sales-intelligence/types/range";

const RANGE_TYPE_FILTER_WIDTH = 117;
const RANGE_FILTER_WIDTH = 138;

const UpdateDateFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseModelProps<IAppsUpdateDateFilterDto, IAppsUpdateDateDescriptionDto>): ReactElement => {
    const { ranges, rangeTypes } = filter.options;

    const [rangeType, setRangeType] = useState<RelativeRangeType | undefined>(rangeTypes?.[0]);

    // Hydrate from the saved value first so a persisted filter shows its stored rangeType,
    // falling back to local state for a fresh selection before anything is saved.
    const selectedRangeType = value?.rangeType ?? rangeType;

    const handleRangeTypeChange = (newValue: RelativeRangeType) => {
        setRangeType(newValue);

        if (!value?.range) {
            return;
        }

        onFilterUpdate({ ...value, rangeType: newValue });
    };

    const handleRangeChange = (newValue: RelativeRange) => {
        if (!selectedRangeType) {
            return;
        }

        onFilterUpdate({ range: newValue, rangeType: selectedRangeType });
    };

    return (
        <FormFieldLabel text={translate("si.find_apps.filters.updateDate.dropdown-label")}>
            <Flex gap="8px">
                <FormSelectCombobox<RelativeRangeType>
                    width={RANGE_TYPE_FILTER_WIDTH}
                    fieldName="updateDateRangeType"
                    value={selectedRangeType}
                    options={rangeTypes}
                    formatValue={(option) => getUpdateDateOptionLabel(option)}
                    onChange={handleRangeTypeChange}
                />
                <FormSelectCombobox<RelativeRange>
                    width={RANGE_FILTER_WIDTH}
                    fieldName="updateDateRange"
                    value={value?.range}
                    options={ranges}
                    placeholder={translate("si.find_apps.filters.updateDate.dropdown-placeholder")}
                    formatValue={(option) => getUpdateDateOptionLabel(option)}
                    onChange={handleRangeChange}
                />
            </Flex>
        </FormFieldLabel>
    );
};

export default UpdateDateFilter;
